import { useMemo } from 'react'
import { type IntlShape, useIntl } from 'react-intl'

import {
  SUPPORT_PERMISSION_BY_LINE,
  type SupportBlocking,
  type SupportCategory,
  type SupportContext,
} from '@onr/schema'
import dayjs from 'dayjs'

import { stringIsMessageId } from '@/l10n/stringIsMessageId'

const SUPPORT_AGENT_PERMISSIONS = new Set<string>(Object.values(SUPPORT_PERMISSION_BY_LINE))

// Mirrors the order of Pilote's user form, UserController.addPermissionsToModel.
const PILOTE_PERMISSION_ORDER = [
  'central_admin',
  'evening_editor',
  'support_requester',
  'evening',
  'assignment',
  'allocation',
  'volunteer_admin',
  'team_admin',
  'team_confidential_note',
  'presence',
  'telephony',
  'user_admin',
  'organization_admin',
  'gift_admin',
  'institution_admin',
  'region_admin',
  'room_admin',
  'background_check',
]

const permissionRank = (permission: string) => {
  const rank = PILOTE_PERMISSION_ORDER.indexOf(permission)
  return rank === -1 ? PILOTE_PERMISSION_ORDER.length : rank
}

export const supportLabels = (intl: IntlShape) => {
  const relative = (date: Date, now: Date) => {
    const minutes = Math.max(0, dayjs(now).diff(date, 'minute'))
    if (minutes < 1) {
      return intl.formatMessage({ id: 'soutien.time.now' })
    }
    if (minutes < 60) {
      return intl.formatMessage({ id: 'soutien.time.minutes' }, { count: minutes })
    }
    const days = dayjs(now).startOf('day').diff(dayjs(date).startOf('day'), 'day')
    if (days === 0) {
      return intl.formatMessage({ id: 'soutien.time.hours' }, { count: Math.floor(minutes / 60) })
    }
    return days === 1
      ? intl.formatMessage({ id: 'soutien.time.yesterday' })
      : intl.formatMessage({ id: 'soutien.time.days' }, { count: days })
  }

  const openedAt = (date: Date, now: Date) => {
    const time = intl.formatTime(date)
    const days = dayjs(now).startOf('day').diff(dayjs(date).startOf('day'), 'day')
    if (days === 0) {
      return intl.formatMessage({ id: 'soutien.time.at' }, { time })
    }
    if (days === 1) {
      return intl.formatMessage({ id: 'soutien.time.yesterdayAt' }, { time })
    }
    return intl.formatMessage(
      { id: 'soutien.time.onDateAt' },
      { date: intl.formatDate(date, { day: 'numeric', month: 'long' }), time }
    )
  }

  const permissions = (codes: ReadonlyArray<string>) => {
    const shown = codes.filter((code) => !SUPPORT_AGENT_PERMISSIONS.has(code))
    if (shown.length === 0) {
      return intl.formatMessage({ id: 'soutien.permissions.none' })
    }
    return shown
      .sort((a, b) => permissionRank(a) - permissionRank(b))
      .map((code) => {
        const id = `soutien.permission.${code}`
        return stringIsMessageId(id) ? intl.formatMessage({ id }) : code
      })
      .join(', ')
  }

  const category = (value: SupportCategory) => intl.formatMessage({ id: `soutien.category.${value}` })
  const blocking = (value: SupportBlocking) => intl.formatMessage({ id: `soutien.blocking.${value}` })

  const you = (context: Pick<SupportContext, 'permissions' | 'centralName'>) =>
    intl.formatMessage(
      { id: 'soutien.context.youValue' },
      { permissions: permissions(context.permissions), central: context.centralName }
    )

  return { relative, openedAt, permissions, category, blocking, you }
}

export const useSupportLabels = () => {
  const intl = useIntl()
  return useMemo(() => supportLabels(intl), [intl])
}
